import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import tools from "../../../../helpers/tools";
import GenererAccordModal from "./GenererAccordModal";
import EnvoyerAccordFournisseurModal from "./EnvoyerAccordFournisseurModal";
import styles from "../FinanceDetailV2.module.scss";

/**
 * LS-657 / LS-668 / LS-687 — génération de l'accord de financement BPCE et
 * envoi au fournisseur. N'est rendu par le parent que si le financeur de la
 * demande a l'option "génération d'accord" activée (LS-666,
 * `Financier.peutGenererAccordFinancement`).
 *
 * Le document généré (`TypeDocument::AccordFinancement`) n'est pas listé par
 * `DocumentsUploader` (type non sélectionnable à l'upload manuel) : cette
 * section charge et affiche sa propre liste, indépendamment.
 *
 * `lignes` (une par devis de la demande, cf. `financeData.quotations` côté
 * parent) alimente l'aperçu de GenererAccordModal et d'EnvoyerAccordFournisseurModal
 * — le contenu réel du PDF/mail est recalculé côté serveur à partir des mêmes devis.
 *
 * `fournisseurUuid` (LS-687, `financeData.accordFournisseurGenereUuid`) — le
 * fournisseur choisi à la génération, dénormalisé côté API : sert de source de
 * vérité pour identifier le fournisseur à l'envoi (carnet d'adresses), y
 * compris après rechargement de page. `dernierFournisseurGenere` (état local,
 * le plus frais) prend le pas dessus juste après une génération dans la même
 * session.
 *
 * `tampons` (LS-689) — ceux de l'utilisateur connecté, proposés au choix dans
 * GenererAccordModal. Le choix n'est pas mémorisé d'une génération à l'autre :
 * l'API ne l'historise pas, seul le PDF produit en porte la trace.
 */
const AccordFinancementSection = ({
  demandeUuid,
  statutEligible,
  disabled,
  lignes = [],
  expediteurNom,
  expediteurSignatureUrl,
  tampons = [],
  fournisseurUuid,
  accordEnvoyeFournisseur,
  onEnvoye,
}) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isGenererOpen, setIsGenererOpen] = useState(false);
  // Fournisseur sélectionné au dernier "Générer" réussi, pour préremplir le
  // destinataire de l'envoi (cf. note ci-dessus).
  const [dernierFournisseurGenere, setDernierFournisseurGenere] =
    useState(null);

  const [isEnvoyerOpen, setIsEnvoyerOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const fournisseurUuidActif =
    dernierFournisseurGenere?.uuid || fournisseurUuid || null;

  useEffect(() => {
    if (!demandeUuid || !statutEligible) return;
    setIsLoading(true);
    financeApiService
      .getDocuments(demandeUuid, "accord_financement")
      .then((res) => setDocuments(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error("Impossible de charger l'accord de financement."))
      .finally(() => setIsLoading(false));
  }, [demandeUuid, statutEligible]);

  const dernierAccord =
    documents.length > 0
      ? [...documents].sort((a, b) =>
          (a.createdAt || "").localeCompare(b.createdAt || ""),
        )[documents.length - 1]
      : null;

  async function handleGenerer(fournisseurUuid, fournisseur, tamponId) {
    try {
      const res = await financeApiService.genererAccord(
        demandeUuid,
        fournisseurUuid,
        tamponId || null,
      );
      setDocuments((prev) => [...prev, res.data]);
      setDernierFournisseurGenere(fournisseur || null);
      toast.success("L'accord de financement a été généré.");
      return true;
    } catch (err) {
      const message =
        err?.response?.status === 403
          ? "Ce financeur n'est pas éligible à la génération de l'accord de financement."
          : err?.response?.data?.message ||
            "Erreur lors de la génération de l'accord de financement.";
      toast.error(message);
      return false;
    }
  }

  function openEnvoyerModal() {
    setIsEnvoyerOpen(true);
  }

  function closeEnvoyerModal() {
    if (isSending) return;
    setIsEnvoyerOpen(false);
  }

  async function handleEnvoyer({ destinataire, cc, commentaire }) {
    setIsSending(true);
    try {
      await financeApiService.envoyerAccordFournisseur(demandeUuid, {
        destinataire,
        cc,
        commentaire,
        fournisseurId: fournisseurUuidActif,
      });
      toast.success("L'accord de financement a été envoyé au fournisseur.");
      if (onEnvoye) await onEnvoye();
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Erreur lors de l'envoi de l'accord au fournisseur.",
      );
      return false;
    } finally {
      setIsSending(false);
    }
  }

  if (!statutEligible) {
    return (
      <div className={styles.financierCardStatusMuted}>
        Disponible une fois la demande envoyée au financeur.
      </div>
    );
  }

  return (
    <div className={styles.documentsSection}>
      {!disabled && (
        <div className={styles.documentsUploadRow}>
          <button
            type="button"
            className={styles.btnSave}
            onClick={() => setIsGenererOpen(true)}
          >
            <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
            {dernierAccord ? "Régénérer l'accord" : "Générer l'accord"}
          </button>
          {dernierAccord && (
            <button
              type="button"
              className={styles.btnEnvoyerFinanceur}
              onClick={openEnvoyerModal}
            >
              <i className="fa fa-envelope" style={{ marginRight: 4 }} />
              Envoyer au fournisseur
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className={styles.financierCardStatusMuted}>Chargement…</div>
      ) : !dernierAccord ? (
        <div className={styles.financierCardStatusMuted}>
          Aucun accord de financement généré.
        </div>
      ) : (
        <ul className={styles.documentsList}>
          <li className={styles.documentItem}>
            <i className={`fa fa-file-pdf-o ${styles.documentIcon}`} />
            <a
              href={dernierAccord.url}
              target="_blank"
              rel="noreferrer"
              className={styles.documentName}
            >
              {dernierAccord.fileName}
            </a>
            <span className={styles.documentMeta}>
              {dernierAccord.createdAt
                ? tools.formatDate(dernierAccord.createdAt)
                : ""}
            </span>
          </li>
        </ul>
      )}

      {accordEnvoyeFournisseur && (
        <div className={styles.recapEnvoyeInfo}>
          <i className="fa fa-check-circle" style={{ marginRight: 6 }} />
          Déjà envoyé au fournisseur — dernier destinataire :{" "}
          {accordEnvoyeFournisseur.destinataire}
        </div>
      )}

      <GenererAccordModal
        isOpen={isGenererOpen}
        lignes={lignes}
        tampons={tampons}
        onSubmit={handleGenerer}
        onClose={() => setIsGenererOpen(false)}
      />

      <EnvoyerAccordFournisseurModal
        isOpen={isEnvoyerOpen}
        lignes={lignes}
        expediteurNom={expediteurNom}
        expediteurSignatureUrl={expediteurSignatureUrl}
        fournisseurUuid={fournisseurUuidActif}
        dernierEnvoi={accordEnvoyeFournisseur}
        onSubmit={handleEnvoyer}
        onClose={closeEnvoyerModal}
      />
    </div>
  );
};

export default AccordFinancementSection;
